import { useContext, useState } from "react"

import { type TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { AdminWhatsNewEffectiveSource } from "@/api/admin-api"

import {
  AlertTriangle,
  Eye,
  FileCode2,
  HelpCircle,
  Pin,
  PinOff,
  RefreshCw,
  Sparkles,
} from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import { WhatsNewPreviewDialog } from "@/features/whats-new/components/whats-new-preview-dialog"
import {
  useAdminWhatsNewVersionsQuery,
  useRefreshAdminWhatsNewMutation,
  useSetAdminWhatsNewPinnedMutation,
} from "@/hooks/use-admin-whats-new"

import { DelegationModeControl } from "./delegation-mode-control"
import { HowItWorksPopoverBody, SkeletonPopoverBody } from "./whats-new-help-popovers"

const T = "adminDashboard.whatsNew"

function effectiveSourceLabel(t: TFunction, source: AdminWhatsNewEffectiveSource): string {
  switch (source) {
    case "pinned":
      return t(`${T}.sourcePinned`, "via customer pin")
    case "manifest":
      return t(`${T}.sourceManifest`, "via global override")
    case "auto":
      return t(`${T}.sourceAuto`, "auto: highest version ≤ this app")
    case "none":
    default:
      return t(`${T}.sourceNone`, "no content available")
  }
}

function overrideHint(
  t: TFunction,
  data: {
    override_version: string | null
    override_present: boolean
    override_filename: string | null
    override_filename_canonical: string
  }
): string {
  const filename = data.override_filename ?? data.override_filename_canonical
  if (data.override_version) {
    return t(
      `${T}.manifestSetHint`,
      "Active. Edit the version field in {{filename}} to switch, or set version to null to deactivate.",
      { filename }
    )
  }
  if (data.override_present) {
    return t(
      `${T}.overrideParkedHint`,
      "Skeleton in place. Edit the version field in {{filename}} to activate.",
      { filename }
    )
  }
  return t(
    `${T}.manifestUnsetHint`,
    "Upload {{filename}} once with the skeleton below. After that, activating an override is a one-field edit.",
    { filename: data.override_filename_canonical }
  )
}

export function WhatsNewManagementPanel() {
  const { t } = useTranslation()
  const isFocusedSurface = useContext(AdminFocusedSurfaceContext)
  const { data, isLoading, isFetching, isError, refetch } = useAdminWhatsNewVersionsQuery()
  const setPinned = useSetAdminWhatsNewPinnedMutation()
  const refresh = useRefreshAdminWhatsNewMutation()
  const refreshing = isFetching || refresh.isPending

  const [previewVersion, setPreviewVersion] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const openPreview = (version: string) => {
    setPreviewVersion(version)
    setPreviewOpen(true)
  }

  const closePreview = (open: boolean) => {
    setPreviewOpen(open)
    if (!open) {
      setPreviewVersion(null)
    }
  }

  const handlePin = async (version: string | null) => {
    try {
      await setPinned.mutateAsync(version)
      toast.success(
        version
          ? t(`${T}.toastPinned`, "Pinned version {{version}}", { version })
          : t(`${T}.toastCleared`, "Cleared pinned version")
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t(`${T}.toastFailed`, "Failed to update pinned version")
      )
    }
  }

  const skeletonFilename = data?.override_filename_canonical ?? "whats-new-override.json"

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            {!isFocusedSurface ? (
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="size-5" />
                {t(`${T}.title`, "What's New")}
              </CardTitle>
            ) : null}
            <div className="flex w-full flex-wrap items-center gap-1 sm:w-auto sm:justify-end">
              {/* Manual refresh — bypasses both the React Query 30s
                  staleTime and the 60s server-side _list_versioned_blobs
                  cache (via ?force=true) so admins can verify override / pin
                  / blob changes immediately after editing the manifest in
                  Azure Portal. */}
              <Button
                aria-label={t(`${T}.refresh`, "Refresh")}
                disabled={refreshing}
                size="sm"
                title={t(`${T}.refresh`, "Refresh")}
                variant="ghost"
                onClick={() => refresh.mutate()}
              >
                <RefreshCw className={`mr-1 size-4 ${refreshing ? "animate-spin" : ""}`} />
                {t(`${T}.refresh`, "Refresh")}
              </Button>

              {/* Override skeleton — reference JSON, kept in a popover so it
                  doesn't clutter the panel during normal use. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    aria-label={t(`${T}.skeletonTitle`, "Override file skeleton")}
                    size="sm"
                    title={t(`${T}.skeletonTitle`, "Override file skeleton")}
                    variant="ghost"
                  >
                    <FileCode2 className="mr-1 size-4" />
                    {t(`${T}.skeletonButton`, "Skeleton")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[calc(100vw-2rem)] max-w-[28rem]">
                  <SkeletonPopoverBody filename={skeletonFilename} t={t} />
                </PopoverContent>
              </Popover>

              {/* "How it works" — collapsed from a permanent Alert into an
                  on-demand popover. Same content, no longer takes vertical space. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    aria-label={t(`${T}.helpTitle`, "How uploads & resolution work")}
                    size="sm"
                    title={t(`${T}.helpTitle`, "How uploads & resolution work")}
                    variant="ghost"
                  >
                    <HelpCircle className="mr-1 size-4" />
                    {t(`${T}.helpButton`, "How it works")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="max-h-[36rem] w-[calc(100vw-2rem)] max-w-[36rem] overflow-y-auto"
                >
                  <HowItWorksPopoverBody t={t} />
                </PopoverContent>
              </Popover>
              <DelegationModeControl capability="whats_new" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t(`${T}.loadErrorTitle`, "Could not load What's New versions")}
              </AlertTitle>
              <AlertDescription>
                {t(
                  `${T}.loadErrorBody`,
                  "Check that the blob container SAS URL is configured and reachable."
                )}{" "}
                <Button className="px-1" variant="link" onClick={() => refetch()}>
                  {t(`${T}.retry`, "Retry")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {data && (
            <>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                {/* Tile 1: Customer pin (Cosmos, per deployment, wins over everything). */}
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">
                    {t(`${T}.pinnedLabel`, "Customer pin")}
                  </div>
                  <div className="flex items-center justify-between gap-2 font-medium">
                    <span className="flex items-center gap-2">
                      {data.pinned_version ??
                        t(`${T}.pinnedNone`, "None — using global override or auto")}
                      {data.pinned_blob_missing && (
                        <Badge className="text-xs" variant="destructive">
                          {t(`${T}.badgeMissing`, "missing")}
                        </Badge>
                      )}
                    </span>
                    {data.pinned_version && (
                      <Button
                        disabled={setPinned.isPending}
                        size="sm"
                        variant="outline"
                        onClick={() => handlePin(null)}
                      >
                        <PinOff className="mr-1 size-3.5" />
                        {t(`${T}.clearPin`, "Clear pin")}
                      </Button>
                    )}
                  </div>
                  {data.pinned_blob_missing && (
                    <div className="text-muted-foreground mt-1 text-xs">
                      {t(
                        `${T}.pinnedMissingHint`,
                        "Pin set but no blob found — falls through to override or auto."
                      )}
                    </div>
                  )}
                </div>
                {/* Tile 2: Global override (whats-new-override.json — for outage broadcasts). */}
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">
                    {t(`${T}.manifestLabel`, "Global override")}
                  </div>
                  <div className="flex items-center gap-2 font-medium">
                    <span>
                      {data.override_version ??
                        (data.override_present
                          ? t(`${T}.overrideParked`, "Parked (skeleton present)")
                          : t(`${T}.none`, "—"))}
                    </span>
                    {data.override_blob_missing && (
                      <Badge className="text-xs" variant="destructive">
                        {t(`${T}.badgeMissing`, "missing")}
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    {data.override_blob_missing
                      ? t(
                          `${T}.overrideMissingHint`,
                          "Override set but no blob found — falls through to auto."
                        )
                      : overrideHint(t, data)}
                  </div>
                </div>
                {/* Tile 3: Effective (what users actually see). */}
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground">
                    {t(`${T}.effectiveLabel`, "Effective")}
                  </div>
                  <div className="font-medium">{data.effective_version ?? t(`${T}.none`, "—")}</div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    {effectiveSourceLabel(t, data.effective_source)}
                    {data.app_version && data.effective_source === "auto" && (
                      <>
                        {" "}
                        {t(`${T}.appVersionSuffix`, "(app: {{appVersion}})", {
                          appVersion: data.app_version,
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {data.warnings.length > 0 && (
                <Alert>
                  <AlertTriangle className="size-4" />
                  <AlertTitle>{t(`${T}.warningsTitle`, "Warnings")}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-5">
                      {data.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {data.versions.length === 0 ? (
                <Alert>
                  <AlertTitle>{t(`${T}.emptyTitle`, "No versioned files found")}</AlertTitle>
                  <AlertDescription>
                    {t(
                      `${T}.emptyBody`,
                      "Upload whats-new-{locale}-{version}.md files to the blob container to populate this list."
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t(`${T}.colVersion`, "Version")}</TableHead>
                      <TableHead>{t(`${T}.colLocales`, "Locales")}</TableHead>
                      <TableHead className="w-[1%] whitespace-nowrap">
                        {t(`${T}.colActions`, "Actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.versions.map((row) => {
                      const isManifest = row.version === data.override_version
                      const isPinned = row.version === data.pinned_version
                      const isAuto =
                        !data.override_version &&
                        !data.pinned_version &&
                        row.version === data.applicable_version
                      return (
                        <TableRow key={row.version}>
                          <TableCell className="font-mono">
                            <div className="flex items-center gap-2">
                              {row.version}
                              {isManifest && (
                                <Badge variant="secondary">
                                  {t(`${T}.badgeGlobalOverride`, "global override")}
                                </Badge>
                              )}
                              {isAuto && (
                                <Badge variant="secondary">{t(`${T}.badgeAuto`, "auto")}</Badge>
                              )}
                              {isPinned && <Badge>{t(`${T}.badgePinned`, "pinned")}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {row.locales.map((loc) => (
                                <Badge key={loc} variant="outline">
                                  {loc}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="space-x-2 whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPreview(row.version)}
                            >
                              <Eye className="mr-1 size-3.5" />
                              {t(`${T}.actionPreview`, "Preview")}
                            </Button>
                            <Button
                              disabled={isPinned || setPinned.isPending}
                              size="sm"
                              variant={isPinned ? "secondary" : "default"}
                              onClick={() => handlePin(row.version)}
                            >
                              <Pin className="mr-1 size-3.5" />
                              {isPinned
                                ? t(`${T}.actionPinned`, "Pinned")
                                : t(`${T}.actionPin`, "Pin")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {previewVersion && (
        <WhatsNewPreviewDialog
          open={previewOpen}
          version={previewVersion}
          onOpenChange={closePreview}
        />
      )}
    </div>
  )
}
